import { apiFetch } from "./api";

export interface MeResponse {
  userId: string;
  email: string;
  displayName: string;
  employeeId: number | null;
  roles: string[];
  permissions: string[];
  mfaEnabled: boolean;
  mfaRequired: boolean;
  defaultRoute: string;
  featureFlags: Record<string, boolean>;
  /**
   * The employee's saved printer defaults, so a print can be routed to the operator's own hardware
   * without a second round trip.
   *
   * `PATCH /api/me` still must not write them: a phase-1 bug had it blanking both columns on every
   * profile save, because the profile page never sends them. Writing goes through the dedicated
   * `savePrinterDefaults` below, whose contract is a replacement of the pair.
   */
  defaultDocketPrinterId: number | null;
  defaultStickerPrinterId: number | null;
}

export const fetchMe = (): Promise<MeResponse> => apiFetch<MeResponse>("/api/me");

/**
 * Stores the caller's own default printers.
 *
 * Self-scoped on purpose: the server resolves the employee from the token, so this needs no claim.
 * The obvious alternative — `PATCH /api/employees/{id}` — needs `employees:write`, which only Admin
 * holds, so the warehouse operators whose "last printer used" this remembers would all 403.
 *
 * Both ids are always sent and both are always written: a null clears a default rather than being
 * ignored, so switching back to "no default" sticks. Callers must therefore pass through the id
 * they are not changing (both are on `MeResponse`).
 */
export const savePrinterDefaults = (defaults: {
  defaultDocketPrinterId: number | null;
  defaultStickerPrinterId: number | null;
}): Promise<void> =>
  apiFetch<void>("/api/me/printer-defaults", { method: "PUT", body: JSON.stringify(defaults) });
